import { useEffect, useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  card: "var(--pr-card)", ink: "var(--pr-ink)", ink2: "var(--pr-ink2)", muted: "var(--pr-muted)",
  line: "var(--pr-line)", gold: "var(--pr-gold)", bg: "var(--pr-bg)",
};

const HEARD = [
  { k: "friend", label: "Friend / word of mouth" },
  { k: "social", label: "Social media" },
  { k: "search", label: "Search" },
  { k: "podcast_press", label: "Podcast / press" },
  { k: "nd_community", label: "An ADHD / neurodivergent community" },
  { k: "other", label: "Other" },
];
const WORK = [
  { k: "freelancer", label: "Freelancer" },
  { k: "gig_platform", label: "Gig / platform worker" },
  { k: "service_provider", label: "Service provider with clients" },
  { k: "other", label: "Other" },
];
const TENURE = ["<6 months", "6-12 months", "1-2 years", "2-5 years", "5-10 years", "10+ years"];
const ND = [
  { k: "yes", label: "Yes" },
  { k: "no", label: "No" },
  { k: "prefer_not", label: "Prefer not to say" },
  { k: "self", label: "Self-describe" },
];

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="px-3.5 py-2 rounded-full text-sm font-semibold transition-transform hover:scale-[1.02]"
    style={active
      ? { background: COLOR.gold, color: COLOR.ink }
      : { background: "var(--pr-bg)", color: COLOR.ink2, border: `1px solid ${COLOR.line}` }}
  >
    {children}
  </button>
);

export const AboutYouCard = () => {
  const [due, setDue] = useState(false);
  const [busy, setBusy] = useState(false);
  const [heardFrom, setHeardFrom] = useState("");
  const [heardOther, setHeardOther] = useState("");
  const [workTypes, setWorkTypes] = useState([]);
  const [workOther, setWorkOther] = useState("");
  const [primaryIncome, setPrimaryIncome] = useState(null);
  const [tenureIdx, setTenureIdx] = useState(0);
  const [nd, setNd] = useState("");
  const [ndText, setNdText] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/caddy/about/status`, { credentials: "include" });
        if (!res.ok) return;
        const d = await res.json();
        setDue(!!d.due);
      } catch { /* stay hidden */ }
    })();
  }, []);

  const toggleWork = (k) =>
    setWorkTypes((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const canSubmit = heardFrom && workTypes.length > 0 && primaryIncome !== null;

  const dismiss = async () => {
    setDue(false);
    try {
      await fetch(`${API}/api/caddy/about/dismiss`, { method: "POST", credentials: "include" });
    } catch { /* already hidden locally */ }
  };

  const submit = async () => {
    if (busy || !canSubmit) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/caddy/about`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          heard_from: heardFrom,
          heard_other: heardFrom === "other" ? heardOther : "",
          work_types: workTypes,
          work_other: workTypes.includes("other") ? workOther : "",
          primary_income: primaryIncome,
          tenure: TENURE[tenureIdx],
          neurodivergent: nd,
          neurodivergent_text: nd === "self" ? ndText : "",
        }),
      });
      if (!res.ok) { toast.error("Couldn't save, please try again."); return; }
      setDue(false);
      toast.success("Thank you — this helps us build Payday Relay for you 🏁");
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!due) return null;

  const Label = ({ children, required }) => (
    <p className="text-sm font-bold mb-2" style={{ color: COLOR.ink }}>
      {children}{required && <span style={{ color: "var(--pr-red, #C0392B)" }}> *</span>}
    </p>
  );

  return (
    <div className="rounded-2xl p-5 border-2 mb-4" style={{ background: "var(--pr-card)", borderColor: COLOR.gold }} data-testid="about-you-card">
      <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: "var(--pr-accent-gold-text, #B8860B)" }}>
        Welcome
      </p>
      <h3 className="text-lg font-bold mb-1" style={{ color: COLOR.ink }}>Tell us a bit about you</h3>
      <p className="text-sm mb-5" style={{ color: COLOR.ink2 }}>
        About 20 seconds. It helps us build Payday Relay for the way you actually work. You can skip for now.
      </p>

      {/* How did you hear */}
      <div className="mb-5">
        <Label required>How did you hear about Payday Relay?</Label>
        <div className="flex flex-wrap gap-2" data-testid="about-heard">
          {HEARD.map((o) => (
            <Chip key={o.k} active={heardFrom === o.k} onClick={() => setHeardFrom(o.k)} testid={`about-heard-${o.k}`}>
              {o.label}
            </Chip>
          ))}
        </div>
        {heardFrom === "other" && (
          <input
            value={heardOther}
            onChange={(e) => setHeardOther(e.target.value)}
            placeholder="Tell us where…"
            data-testid="about-heard-other"
            className="mt-2 w-full px-3 py-2 rounded-lg text-sm"
            style={{ background: "var(--pr-bg)", border: `1px solid ${COLOR.line}`, color: COLOR.ink }}
          />
        )}
      </div>

      {/* Work type */}
      <div className="mb-5">
        <Label required>What kind of work do you do? <span className="font-normal" style={{ color: COLOR.muted }}>(pick all that fit)</span></Label>
        <div className="flex flex-wrap gap-2" data-testid="about-work">
          {WORK.map((o) => (
            <Chip key={o.k} active={workTypes.includes(o.k)} onClick={() => toggleWork(o.k)} testid={`about-work-${o.k}`}>
              {o.label}
            </Chip>
          ))}
        </div>
        {workTypes.includes("other") && (
          <input
            value={workOther}
            onChange={(e) => setWorkOther(e.target.value)}
            placeholder="Tell us what kind of work…"
            data-testid="about-work-other"
            className="mt-2 w-full px-3 py-2 rounded-lg text-sm"
            style={{ background: "var(--pr-bg)", border: `1px solid ${COLOR.line}`, color: COLOR.ink }}
          />
        )}
      </div>

      {/* Primary income */}
      <div className="mb-5">
        <Label required>Is this work your primary source of income?</Label>
        <div className="flex gap-2" data-testid="about-primary">
          <Chip active={primaryIncome === true} onClick={() => setPrimaryIncome(true)} testid="about-primary-yes">Yes</Chip>
          <Chip active={primaryIncome === false} onClick={() => setPrimaryIncome(false)} testid="about-primary-no">No</Chip>
        </div>
      </div>

      {/* Tenure slider */}
      <div className="mb-5">
        <Label required>How long have you been doing this kind of work?</Label>
        <input
          type="range"
          min={0}
          max={TENURE.length - 1}
          step={1}
          value={tenureIdx}
          onChange={(e) => setTenureIdx(Number(e.target.value))}
          data-testid="about-tenure-slider"
          className="w-full"
          style={{ accentColor: COLOR.gold }}
        />
        <div className="flex justify-between mt-1">
          <span className="text-xs" style={{ color: COLOR.muted }}>{TENURE[0]}</span>
          <span className="text-sm font-bold" style={{ color: COLOR.ink }} data-testid="about-tenure-value">{TENURE[tenureIdx]}</span>
          <span className="text-xs" style={{ color: COLOR.muted }}>{TENURE[TENURE.length - 1]}</span>
        </div>
      </div>

      {/* Neurodivergence (optional) */}
      <div className="mb-5">
        <Label>Do you identify as neurodivergent? <span className="font-normal" style={{ color: COLOR.muted }}>(optional)</span></Label>
        <p className="text-xs mb-2" style={{ color: COLOR.muted }}>
          We ask so we can build for how your brain works. It's voluntary, and only ever shown anonymously in groups of 5+.
        </p>
        <div className="flex flex-wrap gap-2" data-testid="about-nd">
          {ND.map((o) => (
            <Chip key={o.k} active={nd === o.k} onClick={() => setNd(nd === o.k ? "" : o.k)} testid={`about-nd-${o.k}`}>
              {o.label}
            </Chip>
          ))}
        </div>
        {nd === "self" && (
          <input
            value={ndText}
            onChange={(e) => setNdText(e.target.value)}
            placeholder="In your own words (optional)"
            data-testid="about-nd-text"
            className="mt-2 w-full px-3 py-2 rounded-lg text-sm"
            style={{ background: "var(--pr-bg)", border: `1px solid ${COLOR.line}`, color: COLOR.ink }}
          />
        )}
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit || busy}
          data-testid="about-submit"
          className="px-5 py-2.5 rounded-full font-bold text-sm transition-transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: COLOR.gold, color: COLOR.ink }}
        >
          {busy ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="about-dismiss"
          className="text-sm font-semibold hover:opacity-70"
          style={{ color: COLOR.ink2 }}
        >
          Maybe later
        </button>
      </div>
    </div>
  );
};

export default AboutYouCard;
